// next-app/src/lib/validations/auth.ts
import { z } from 'zod';
import { emailSchema, passwordSchema, requiredSchema } from './validators';

// Regex patterns (using .refine() instead of .regex() to avoid Turbopack bundling issues)
const partsCodeRegex = /^[0-9]+$/;
const addressRegex = /^\S[a-zA-Z0-9\s,'-/]*$/;
const cityRegex = /^\S+[a-zA-Z ,.'-]+$/;
const zipRegex =
  /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$)/;

// ============================================
// LOGIN FORM
// Reference: src/pages/Login.tsx
// ============================================

export const loginSchema = z.object({
  email: emailSchema,
  password: requiredSchema,
});

export type LoginFormData = z.infer<typeof loginSchema>;

// ============================================
// EVENT PORTAL LOGIN FORM
// Reference: Event staff login with Event ID + Password
// ============================================

export const eventPortalLoginSchema = z.object({
  eventId: requiredSchema,
  password: requiredSchema,
});

export type EventPortalLoginFormData = z.infer<typeof eventPortalLoginSchema>;

// ============================================
// SIGNUP FORM
// Reference: src/pages/SignUp.tsx
// ============================================

export const signupSchema = z
  .object({
    // Retailer Information
    parts_code: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => partsCodeRegex.test(val), 'Please enter valid sales code.'),
    dealer_region: z.enum(['CE', 'EMA', 'GL', 'SE', 'W'], {
      message: 'Please select a market area',
    }),
    dealer_name: requiredSchema.max(100),
    address: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => addressRegex.test(val), 'Please enter valid address.')
      .max(45),
    address2: z.string().max(45).optional().or(z.literal('')),
    city: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => cityRegex.test(val), 'Please enter valid city.')
      .max(45),
    state: requiredSchema,
    zip: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => zipRegex.test(val), 'Please enter valid zip code.'),
    phone: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => {
        const digits = val.replace(/\D/g, '');
        return digits.length === 10;
      }, 'Phone number must be exactly 10 digits.'),
    // Point of Contact (First Name, Last Name, Phone are REQUIRED per RD1 feedback)
    contact_first_name: z.string().min(1, 'This field is required').max(45),
    contact_last_name: z.string().min(1, 'This field is required').max(45),
    contact_phone: z
      .string()
      .min(1, 'This field is required')
      .refine((val) => {
        const digits = val.replace(/\D/g, '');
        return digits.length === 10;
      }, 'Phone number must be exactly 10 digits.'),
    contact_email: emailSchema,
    // Additional Info
    notes: z.string().max(1000).optional().or(z.literal('')),
    // Terms & Conditions
    agree_terms: z
      .boolean()
      .refine((val) => val === true, 'You must agree to the Terms & Conditions'),
    // Password
    password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords must match',
    path: ['confirm_password'],
  });

export type SignupFormData = z.infer<typeof signupSchema>;

// ============================================
// FORGOT PASSWORD FORM
// Reference: src/pages/ForgotPassword.tsx
// ============================================

export const forgotPasswordSchema = z.object({
  contact_email: emailSchema,
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

// ============================================
// RESET PASSWORD FORM
// Reference: src/pages/ResetPassword.tsx
// ============================================

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    password2: z.string(),
  })
  .refine((data) => data.password === data.password2, {
    message: 'Passwords must match',
    path: ['password2'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
